// ABOUTME: Label/value facts of the job detail: rows for the job, a compact two-column grid for the
// ABOUTME: selected attempt. Entries without a value are left out, so no empty cell is rendered.

import { Box, Typography } from '@ds/coolshop';
import { ReactNode } from 'react';

export type InfoEntry = {
    key: string;
    label: string;
    value: ReactNode;
    /** In the grid variant, takes a whole row: for values too long for half of it (dates). */
    wide?: boolean;
};

const hasValue = (value: ReactNode) => value !== null && value !== undefined && value !== '';

interface IInfoListProps {
    entries: InfoEntry[];
    /**
     * `rows` (default): one row per entry, the label on the left and the value right-aligned.
     * `grid`: two columns of small facts, each label above its value, so they read apart from
     * the rows around them.
     */
    variant?: 'rows' | 'grid';
}

export function InfoList(props: Readonly<IInfoListProps>) {
    const { entries, variant = 'rows' } = props;
    if (variant === 'grid') {
        return (
            // Dense flow lets a short fact fill the half row a wide one left empty.
            <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gridAutoFlow="row dense"
                columnGap={2}
                rowGap={1.5}
            >
                {entries
                    .filter(entry => hasValue(entry.value))
                    .map(entry => (
                        <Box
                            key={entry.key}
                            display="flex"
                            flexDirection="column"
                            minWidth={0}
                            gridColumn={entry.wide ? '1 / -1' : undefined}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {entry.label}
                            </Typography>
                            {typeof entry.value === 'string' || typeof entry.value === 'number' ? (
                                <Typography variant="caption" wordBreak="break-word">
                                    {entry.value}
                                </Typography>
                            ) : (
                                entry.value
                            )}
                        </Box>
                    ))}
            </Box>
        );
    }
    return (
        <Box display="flex" flexDirection="column" gap={1}>
            {entries
                .filter(entry => hasValue(entry.value))
                .map(entry => (
                    <Box
                        key={entry.key}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        minHeight={24}
                    >
                        {/* A long label wraps; the value keeps its room on the right. */}
                        <Box minWidth={0}>
                            <Typography variant="body2" color="text.secondary">
                                {entry.label}
                            </Typography>
                        </Box>
                        <Box flexShrink={0} maxWidth="60%" display="flex" justifyContent="flex-end">
                            {typeof entry.value === 'string' || typeof entry.value === 'number' ? (
                                <Typography
                                    variant="body2"
                                    textAlign="right"
                                    wordBreak="break-word"
                                >
                                    {entry.value}
                                </Typography>
                            ) : (
                                entry.value
                            )}
                        </Box>
                    </Box>
                ))}
        </Box>
    );
}
